import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, apiDelete, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Toast, type ToastVariant } from '../components/Toast';

interface Props { user: AuthUser }

interface ReportDef {
  id: string; report_code: string | null; name_en: string; name_ms: string;
  report_type: 'PREDEFINED' | 'CUSTOM'; entity_type: string;
  scope: string; minimum_role: string; is_shared: boolean;
  school_id: string | null; created_at: string;
}

interface EntityType {
  key: string; label_en: string; label_ms: string; fields: number;
}

interface FieldSchema {
  key: string; label_en: string; label_ms: string; type: string;
}

interface RunResult {
  jobId: string; data: Record<string, unknown>[]; total: number; columns: string[];
}

interface ScheduleInfo {
  id: string; definition_id: string; frequency: string;
  day_of_week: number | null; day_of_month: number | null;
  recipients: string[]; last_run_at: string | null; last_status: string | null;
  is_active: boolean; created_at: string;
}

const SCOPE_COLOR: Record<string, string> = { MOE: '#facc15', SCHOOL: '#4ade80', DISTRICT: '#60a5fa' };
const DAYS_MS = ['Ahad', 'Isnin', 'Selasa', 'Rabu', 'Khamis', 'Jumaat', 'Sabtu'];
const FREQ_LABELS: Record<string, string> = { DAILY: 'Harian', WEEKLY: 'Mingguan', MONTHLY: 'Bulanan', CUSTOM: 'Tersuai' };

function downloadCsv(columns: string[], data: Record<string, unknown>[], filename: string) {
  const header = columns.join(',');
  const rows = data.map((row) =>
    columns.map((col) => {
      const v = row[col] ?? '';
      const s = String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(','),
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export function ReportsPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  const [tab, setTab] = useState<'predefined' | 'custom' | 'builder'>('predefined');

  // Report list
  const [reports,    setReports]    = useState<ReportDef[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [toast,      setToast]      = useState<{ message: string; variant: ToastVariant } | null>(null);

  // Run panel
  const [running,    setRunning]    = useState<string | null>(null);
  const [runResult,  setRunResult]  = useState<RunResult | null>(null);
  const [runError,   setRunError]   = useState<string | null>(null);
  const [runningId,  setRunningId]  = useState<string | null>(null);
  const [filters, setFilters] = useState({
    schoolId: '', district: '', dateFrom: '', dateTo: '', classId: '', subjectCode: '', level: '',
  });

  // Schedule panel
  const [scheduling,     setScheduling]     = useState<ReportDef | null>(null);
  const [schedules,      setSchedules]      = useState<ScheduleInfo[]>([]);
  const [schedLoading,   setSchedLoading]   = useState(false);
  const [schedForm, setSchedForm] = useState({ frequency: 'WEEKLY', dayOfWeek: '1', dayOfMonth: '1', recipients: '' });
  const [schedError,     setSchedError]     = useState<string | null>(null);
  const [savingSched,    setSavingSched]     = useState(false);
  const [deletingSched,  setDeletingSched]   = useState<string | null>(null);

  // Builder
  const [entityTypes,  setEntityTypes]  = useState<EntityType[]>([]);
  const [entityFields, setEntityFields] = useState<FieldSchema[]>([]);
  const [builderForm, setBuilderForm] = useState({
    nameEn: '', nameMs: '', entityType: '', scope: 'SCHOOL', isShared: false,
  });
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [builderError,   setBuilderError]   = useState<string | null>(null);
  const [savingBuilder,  setSavingBuilder]  = useState(false);
  const [entityLoading,  setEntityLoading]  = useState(false);

  // Delete
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const isAdmin = user.roles.some((r) => ['admin', 'super_admin'].includes(r.role));
  const isMoe   = user.roles.some((r) => ['super_admin', 'moe_admin'].includes(r.role));

  const predefined = reports.filter((r) => r.report_type === 'PREDEFINED');
  const custom     = reports.filter((r) => r.report_type === 'CUSTOM');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setReports(await apiGet<ReportDef[]>('/api/reports')); }
    catch (err) { setError(err instanceof Error ? err.message : 'Gagal memuatkan laporan. / Failed to load reports.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (tab !== 'builder') return;
    void (async () => {
      try { setEntityTypes(await apiGet<EntityType[]>('/api/reports/builder/entities')); }
      catch { /**/ }
    })();
  }, [tab]);

  async function loadEntityFields(entityType: string) {
    if (!entityType) { setEntityFields([]); return; }
    setEntityLoading(true);
    try {
      const schema = await apiGet<{ fields: FieldSchema[] }>(`/api/reports/builder/schema/${entityType}`);
      setEntityFields(schema.fields);
      setSelectedFields([]);
    } catch (err) {
      setToast({ variant: 'error', message: err instanceof Error ? err.message : 'Gagal memuatkan skema. / Failed to load schema.' });
      setEntityFields([]);
    } finally { setEntityLoading(false); }
  }

  // ── Run ───────────────────────────────────────────────────────────────────

  function openRun(rep: ReportDef) {
    setRunning(rep.id);
    setRunResult(null); setRunError(null);
    setFilters({ schoolId: '', district: '', dateFrom: '', dateTo: '', classId: '', subjectCode: '', level: '' });
  }

  function validateFilters(): string | null {
    if (filters.dateFrom && filters.dateTo && filters.dateTo < filters.dateFrom)
      return 'Tarikh akhir mesti selepas tarikh mula. / End date must be after start date.';
    return null;
  }

  async function handleRun(reportId: string) {
    const err = validateFilters();
    if (err) { setRunError(err); return; }
    setRunningId(reportId); setRunError(null); setRunResult(null);
    try {
      const f: Record<string, string> = {};
      if (filters.schoolId)   f['schoolId']    = filters.schoolId.trim();
      if (filters.district)   f['district']    = filters.district.trim();
      if (filters.dateFrom)   f['dateFrom']    = filters.dateFrom;
      if (filters.dateTo)     f['dateTo']      = filters.dateTo;
      if (filters.classId)    f['classId']     = filters.classId.trim();
      if (filters.subjectCode)f['subjectCode'] = filters.subjectCode.trim();
      if (filters.level)      f['level']       = filters.level.trim();
      const result = await apiPost<RunResult>(`/api/reports/${reportId}/run`, { filters: Object.keys(f).length ? f : undefined });
      setRunResult(result);
    } catch (e) {
      setRunError(e instanceof Error ? e.message : 'Laporan gagal. / Report failed.');
    } finally { setRunningId(null); }
  }

  // ── Schedule ──────────────────────────────────────────────────────────────

  async function openSchedule(rep: ReportDef) {
    setScheduling(rep); setSchedError(null);
    setSchedForm({ frequency: 'WEEKLY', dayOfWeek: '1', dayOfMonth: '1', recipients: '' });
    setSchedLoading(true);
    try { setSchedules(await apiGet<ScheduleInfo[]>(`/api/reports/${rep.id}/schedules`)); }
    catch { setSchedules([]); }
    finally { setSchedLoading(false); }
  }

  function validateSchedule(): string | null {
    if (!schedForm.recipients.trim()) return 'Sekurang-kurangnya satu penerima diperlukan. / At least one recipient required.';
    const emails = schedForm.recipients.split(',').map((e) => e.trim()).filter(Boolean);
    const badEmail = emails.find((e) => !e.includes('@'));
    if (badEmail) return `E-mel tidak sah: "${badEmail}". / Invalid email: "${badEmail}".`;
    return null;
  }

  async function handleCreateSchedule() {
    if (!scheduling) return;
    const err = validateSchedule();
    if (err) { setSchedError(err); return; }
    setSavingSched(true); setSchedError(null);
    try {
      const emails = schedForm.recipients.split(',').map((e) => e.trim()).filter(Boolean);
      await apiPost('/api/reports/schedules', {
        definitionId: scheduling.id,
        frequency:    schedForm.frequency,
        dayOfWeek:    schedForm.frequency === 'WEEKLY'  ? parseInt(schedForm.dayOfWeek)  : undefined,
        dayOfMonth:   schedForm.frequency === 'MONTHLY' ? parseInt(schedForm.dayOfMonth) : undefined,
        recipients:   emails,
      });
      setToast({ variant: 'success', message: 'Jadual laporan berjaya ditetapkan. / Report schedule created.' });
      setSchedules(await apiGet<ScheduleInfo[]>(`/api/reports/${scheduling.id}/schedules`));
    } catch (e) {
      setSchedError(e instanceof Error ? e.message : 'Gagal menetapkan jadual. / Failed to schedule.');
    } finally { setSavingSched(false); }
  }

  async function handleDeleteSchedule(schedId: string) {
    setDeletingSched(schedId);
    try {
      await apiDelete(`/api/reports/schedules/${schedId}`);
      setSchedules((prev) => prev.filter((s) => s.id !== schedId));
      setToast({ variant: 'success', message: 'Jadual dipadam. / Schedule deleted.' });
    } catch (e) {
      setToast({ variant: 'error', message: e instanceof Error ? e.message : 'Gagal memadam jadual.' });
    } finally { setDeletingSched(null); }
  }

  // ── Builder ───────────────────────────────────────────────────────────────

  function validateBuilder(): string | null {
    if (!builderForm.nameEn.trim()) return 'Nama laporan (EN) diperlukan. / Report name (EN) required.';
    if (!builderForm.nameMs.trim()) return 'Nama laporan (BM) diperlukan. / Report name (BM) required.';
    if (!builderForm.entityType)    return 'Jenis entiti diperlukan. / Entity type required.';
    if (selectedFields.length === 0) return 'Pilih sekurang-kurangnya satu medan. / Select at least one field.';
    return null;
  }

  async function handleSaveBuilder() {
    const err = validateBuilder();
    if (err) { setBuilderError(err); return; }
    setSavingBuilder(true); setBuilderError(null);
    try {
      const fieldConfig = selectedFields.map((key) => {
        const f = entityFields.find((ef) => ef.key === key);
        return { key, label_en: f?.label_en ?? key, label_ms: f?.label_ms ?? key, type: f?.type ?? 'string' };
      });
      await apiPost('/api/reports', {
        nameEn:       builderForm.nameEn.trim(),
        nameMs:       builderForm.nameMs.trim(),
        entityType:   builderForm.entityType,
        scope:        builderForm.scope,
        fieldConfig,
        isShared:     builderForm.isShared,
      });
      setToast({ variant: 'success', message: 'Laporan tersuai berjaya dicipta. / Custom report created.' });
      setBuilderForm({ nameEn: '', nameMs: '', entityType: '', scope: 'SCHOOL', isShared: false });
      setSelectedFields([]); setEntityFields([]);
      void load();
      setTab('custom');
    } catch (e) {
      setBuilderError(e instanceof Error ? e.message : 'Gagal menyimpan laporan. / Failed to save report.');
    } finally { setSavingBuilder(false); }
  }

  // ── Delete custom ─────────────────────────────────────────────────────────

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await apiDelete(`/api/reports/${id}`);
      setReports((prev) => prev.filter((r) => r.id !== id));
      setToast({ variant: 'success', message: 'Laporan dipadam. / Report deleted.' });
    } catch (e) {
      setToast({ variant: 'error', message: e instanceof Error ? e.message : 'Gagal memadam. / Delete failed.' });
    } finally { setDeletingId(null); }
  }

  const shownList = tab === 'predefined' ? predefined : custom;

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '1060px', margin: '0 auto' }}>
          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>← Laman Utama</Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                📊 Enjin Laporan / Reporting Engine
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {predefined.length} laporan pratakrif · {custom.length} laporan tersuai
              </p>
            </div>
            {isMoe && (
              <Link to="/admin/moe-dashboard" style={{ textDecoration: 'none' }}>
                <Button variant="primary" size="sm">🏛 Papan Pemuka MOE</Button>
              </Link>
            )}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, marginBottom: spacing.lg, backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, overflow: 'hidden' }}>
            {([['predefined','📋 Laporan Pratakrif'],['custom','🔧 Laporan Tersuai'],['builder','🏗 Pembina Laporan']] as const).map(([t, l]) => (
              <button key={t} onClick={() => setTab(t)} style={{
                flex: 1, padding: `${spacing.sm} ${spacing.xs}`, background: 'none',
                borderBottom: tab === t ? `2px solid ${colors.primary.yellow}` : '2px solid transparent',
                borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                color: tab === t ? colors.primary.yellow : colors.neutral.gray400,
                fontWeight: tab === t ? typography.fontWeight.semibold : typography.fontWeight.medium,
                fontSize: typography.fontSize.sm, cursor: 'pointer', fontFamily: typography.fontFamily.body,
              }}>{l}</button>
            ))}
          </div>

          {/* Run modal */}
          {running && (() => {
            const rep = reports.find((r) => r.id === running);
            if (!rep) return null;
            return (
              <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 50, padding: spacing.md, overflowY: 'auto' }}>
                <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, width: '100%', maxWidth: '760px', boxShadow: shadows.xl, marginTop: spacing.xl }}>
                  <p style={{ margin: `0 0 ${spacing.xs}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, fontSize: typography.fontSize.lg }}>
                    📊 {rep.name_ms}
                  </p>
                  <p style={{ margin: `0 0 ${spacing.lg}`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{rep.name_en}</p>

                  <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.neutral.gray400 }}>PENAPIS / FILTERS</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                    <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                      <LF label="ID Sekolah" style={{ flex: '2 1 180px' }}>
                        <input type="text" value={filters.schoolId} onChange={(e) => setFilters((f) => ({ ...f, schoolId: e.target.value }))} placeholder="UUID sekolah" disabled={!!runningId} style={inputSt()} />
                      </LF>
                      <LF label="Daerah / District" style={{ flex: '1 1 130px' }}>
                        <select value={filters.district} onChange={(e) => setFilters((f) => ({ ...f, district: e.target.value }))} disabled={!!runningId} style={inputSt()}>
                          <option value="">Semua</option>
                          {['brunei-muara','tutong','belait','temburong'].map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </LF>
                      <LF label="Peringkat / Level" style={{ flex: '1 1 110px' }}>
                        <select value={filters.level} onChange={(e) => setFilters((f) => ({ ...f, level: e.target.value }))} disabled={!!runningId} style={inputSt()}>
                          <option value="">Semua</option>
                          {['primary','secondary','tertiary'].map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </LF>
                    </div>
                    <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                      <LF label="Tarikh Mula" style={{ flex: '1 1 140px' }}>
                        <input type="date" value={filters.dateFrom} max={filters.dateTo || undefined} onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))} disabled={!!runningId} style={inputSt()} />
                      </LF>
                      <LF label="Tarikh Akhir" style={{ flex: '1 1 140px' }}>
                        <input type="date" value={filters.dateTo} min={filters.dateFrom || undefined} onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))} disabled={!!runningId} style={inputSt()} />
                      </LF>
                      <LF label="Kelas / Class ID" style={{ flex: '1 1 140px' }}>
                        <input type="text" value={filters.classId} onChange={(e) => setFilters((f) => ({ ...f, classId: e.target.value }))} disabled={!!runningId} style={inputSt()} />
                      </LF>
                      <LF label="Kod Subjek" style={{ flex: '1 1 120px' }}>
                        <input type="text" value={filters.subjectCode} onChange={(e) => setFilters((f) => ({ ...f, subjectCode: e.target.value }))} disabled={!!runningId} style={inputSt()} />
                      </LF>
                    </div>
                  </div>

                  {runError && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{runError}</p>}

                  <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
                    <Button variant="primary" size="sm" onClick={() => void handleRun(rep.id)} disabled={!!runningId} loading={!!runningId}>
                      {runningId === rep.id ? 'Menjalankan…' : '▶ Jalankan / Run'}
                    </Button>
                    {isAdmin && (
                      <Button variant="secondary" size="sm" onClick={() => { setRunning(null); void openSchedule(rep); }} disabled={!!runningId}>
                        🕐 Jadualkan
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setRunning(null); setRunResult(null); setRunError(null); }} disabled={!!runningId}>Tutup</Button>
                  </div>

                  {/* Results */}
                  {runResult && (
                    <div style={{ marginTop: spacing.lg }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm, flexWrap: 'wrap', gap: spacing.sm }}>
                        <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>
                          ✅ {runResult.total} baris dikembalikan / rows returned
                        </p>
                        {runResult.total > 0 && (
                          <Button variant="ghost" size="sm" onClick={() => downloadCsv(runResult.columns, runResult.data, `${rep.report_code ?? rep.id}_${new Date().toISOString().slice(0,10)}.csv`)}>
                            ⬇ Muat Turun CSV
                          </Button>
                        )}
                      </div>
                      {runResult.total === 0 ? (
                        <div style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: borderRadius.lg, padding: spacing.lg, textAlign: 'center' }}>
                          <p style={{ margin: 0, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>Tiada data ditemui untuk penapis ini. / No data found for these filters.</p>
                        </div>
                      ) : (
                        <div style={{ overflowX: 'auto', borderRadius: borderRadius.lg, border: `1px solid ${colors.neutral.gray800}` }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: typography.fontSize.xs }}>
                            <thead>
                              <tr style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                                {runResult.columns.map((col) => (
                                  <th key={col} style={{ padding: `${spacing.xs} ${spacing.sm}`, textAlign: 'left', color: colors.neutral.gray400, fontWeight: typography.fontWeight.semibold, whiteSpace: 'nowrap', borderBottom: `1px solid ${colors.neutral.gray800}` }}>
                                    {col.replace(/_/g, ' ').toUpperCase()}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {runResult.data.slice(0, 100).map((row, i) => (
                                <tr key={i} style={{ borderBottom: `1px solid ${colors.neutral.gray800}`, backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                                  {runResult.columns.map((col) => (
                                    <td key={col} style={{ padding: `${spacing.xs} ${spacing.sm}`, color: colors.neutral.white, whiteSpace: 'nowrap', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {row[col] === null || row[col] === undefined ? <span style={{ color: colors.neutral.gray600 }}>—</span> : String(row[col])}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {runResult.total > 100 && (
                            <p style={{ margin: `${spacing.xs} ${spacing.sm}`, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                              Menunjukkan 100/{runResult.total} baris. Muat turun CSV untuk data penuh.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Schedule modal */}
          {scheduling && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: spacing.md }}>
              <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, width: '100%', maxWidth: '520px', boxShadow: shadows.xl, maxHeight: '90vh', overflowY: 'auto' }}>
                <p style={{ margin: `0 0 ${spacing.lg}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                  🕐 Jadualkan Laporan / Schedule Report
                </p>
                <p style={{ margin: `0 0 ${spacing.md}`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{scheduling.name_ms}</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                  <LF label="Kekerapan / Frequency">
                    <select value={schedForm.frequency} onChange={(e) => setSchedForm((f) => ({ ...f, frequency: e.target.value }))} disabled={savingSched} style={inputSt()}>
                      {Object.entries(FREQ_LABELS).map(([v, l]) => <option key={v} value={v}>{l} ({v})</option>)}
                    </select>
                  </LF>
                  {schedForm.frequency === 'WEEKLY' && (
                    <LF label="Hari Minggu / Day of Week">
                      <select value={schedForm.dayOfWeek} onChange={(e) => setSchedForm((f) => ({ ...f, dayOfWeek: e.target.value }))} disabled={savingSched} style={inputSt()}>
                        {DAYS_MS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                      </select>
                    </LF>
                  )}
                  {schedForm.frequency === 'MONTHLY' && (
                    <LF label="Hari Bulan / Day of Month">
                      <input type="number" value={schedForm.dayOfMonth} min={1} max={28} onChange={(e) => setSchedForm((f) => ({ ...f, dayOfMonth: e.target.value }))} disabled={savingSched} style={inputSt()} />
                    </LF>
                  )}
                  <LF label="Penerima (e-mel, pisahkan dengan koma) *">
                    <input type="text" value={schedForm.recipients} onChange={(e) => setSchedForm((f) => ({ ...f, recipients: e.target.value }))}
                      placeholder="user@example.com, another@example.com" disabled={savingSched} style={inputSt()} />
                  </LF>
                </div>

                {schedError && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{schedError}</p>}
                <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
                  <Button variant="primary" size="sm" onClick={() => void handleCreateSchedule()} disabled={savingSched} loading={savingSched}>
                    {savingSched ? 'Menyimpan…' : 'Simpan Jadual / Save Schedule'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setScheduling(null); setSchedError(null); }} disabled={savingSched}>Tutup</Button>
                </div>

                {/* Existing schedules */}
                {schedLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: spacing.lg }}><Spinner size="sm" /></div>
                ) : schedules.length > 0 && (
                  <div style={{ marginTop: spacing.lg }}>
                    <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.neutral.gray400 }}>JADUAL SEDIA ADA</p>
                    {schedules.map((s) => (
                      <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: spacing.sm, borderRadius: borderRadius.md, backgroundColor: 'rgba(255,255,255,0.04)', marginBottom: spacing.xs }}>
                        <div>
                          <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.white }}>
                            {FREQ_LABELS[s.frequency] ?? s.frequency}
                            {s.day_of_week !== null ? ` — ${DAYS_MS[s.day_of_week] ?? s.day_of_week}` : ''}
                            {s.day_of_month !== null ? ` — Hari ${s.day_of_month}` : ''}
                          </p>
                          <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                            {Array.isArray(s.recipients) ? s.recipients.join(', ') : ''}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" disabled={deletingSched === s.id} onClick={() => void handleDeleteSchedule(s.id)}>
                          {deletingSched === s.id ? <Spinner size="sm" /> : '🗑'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Predefined / Custom list ── */}
          {(tab === 'predefined' || tab === 'custom') && (
            <>
              {error && <ErrorBanner msg={error} onRetry={() => void load()} />}
              {loading && <LoadingBox />}
              {!loading && !error && shownList.length === 0 && (
                <EmptyBox
                  icon={tab === 'predefined' ? '📋' : '🔧'}
                  title={tab === 'predefined' ? 'Tiada Laporan Pratakrif' : 'Tiada Laporan Tersuai'}
                  body={tab === 'predefined' ? 'No predefined reports found.' : 'No custom reports yet. Use the Builder tab to create one.'}
                />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                {shownList.map((rep) => (
                  <div key={rep.id} style={{
                    backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl,
                    border: `1px solid ${colors.neutral.gray800}`, boxShadow: shadows.lg, overflow: 'hidden',
                  }}>
                    <div style={{ padding: spacing.lg, display: 'flex', gap: spacing.md, justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
                          {rep.report_code && <Pill label={rep.report_code} color={colors.primary.yellow} />}
                          <Pill label={rep.scope} color={SCOPE_COLOR[rep.scope] ?? '#9ca3af'} />
                          <Pill label={rep.entity_type} color="#a78bfa" />
                          {rep.is_shared && <Pill label="Dikongsi" color="#60a5fa" />}
                        </div>
                        <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{rep.name_ms}</p>
                        <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{rep.name_en}</p>
                        <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                          Peranan diperlukan: {rep.minimum_role}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: spacing.xs, flexShrink: 0, flexWrap: 'wrap' }}>
                        <Button variant="primary" size="sm" onClick={() => openRun(rep)}>
                          ▶ Jalankan
                        </Button>
                        {isAdmin && (
                          <Button variant="secondary" size="sm" onClick={() => void openSchedule(rep)}>
                            🕐 Jadual
                          </Button>
                        )}
                        {rep.report_type === 'CUSTOM' && isAdmin && (
                          <Button variant="ghost" size="sm" disabled={deletingId === rep.id} onClick={() => void handleDelete(rep.id)}>
                            {deletingId === rep.id ? <Spinner size="sm" /> : '🗑'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Builder tab ── */}
          {tab === 'builder' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
              <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl }}>
                <p style={{ margin: `0 0 ${spacing.lg}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                  🏗 Pembina Laporan Tersuai / Custom Report Builder
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                  <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                    <LF label="Nama Laporan (EN) *" style={{ flex: '2 1 220px' }}>
                      <input type="text" value={builderForm.nameEn} onChange={(e) => setBuilderForm((f) => ({ ...f, nameEn: e.target.value }))}
                        disabled={savingBuilder} style={inputSt()} maxLength={300} />
                    </LF>
                    <LF label="Nama Laporan (BM) *" style={{ flex: '2 1 220px' }}>
                      <input type="text" value={builderForm.nameMs} onChange={(e) => setBuilderForm((f) => ({ ...f, nameMs: e.target.value }))}
                        disabled={savingBuilder} style={inputSt()} maxLength={300} />
                    </LF>
                  </div>
                  <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <LF label="Jenis Entiti *" style={{ flex: '2 1 180px' }}>
                      <select value={builderForm.entityType} onChange={(e) => { setBuilderForm((f) => ({ ...f, entityType: e.target.value })); void loadEntityFields(e.target.value); }}
                        disabled={savingBuilder} style={inputSt()}>
                        <option value="">-- Pilih Entiti --</option>
                        {entityTypes.map((et) => <option key={et.key} value={et.key}>{et.label_ms} / {et.label_en} ({et.fields} medan)</option>)}
                      </select>
                    </LF>
                    <LF label="Skop" style={{ flex: '1 1 120px' }}>
                      <select value={builderForm.scope} onChange={(e) => setBuilderForm((f) => ({ ...f, scope: e.target.value }))} disabled={savingBuilder} style={inputSt()}>
                        <option value="SCHOOL">SCHOOL</option>
                        {isMoe && <option value="MOE">MOE</option>}
                        <option value="DISTRICT">DISTRICT</option>
                      </select>
                    </LF>
                    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, paddingBottom: '2px' }}>
                      <input type="checkbox" id="isShared" checked={builderForm.isShared} onChange={(e) => setBuilderForm((f) => ({ ...f, isShared: e.target.checked }))} disabled={savingBuilder} />
                      <label htmlFor="isShared" style={{ fontSize: typography.fontSize.sm, color: colors.neutral.white, cursor: 'pointer' }}>Kongsi / Shared</label>
                    </div>
                  </div>

                  {/* Field picker */}
                  {entityLoading && <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.md }}><Spinner size="sm" /></div>}
                  {!entityLoading && entityFields.length > 0 && (
                    <div>
                      <label style={labelSt}>Pilih Medan / Select Fields * ({selectedFields.length} dipilih)</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs }}>
                        {entityFields.map((f) => {
                          const sel = selectedFields.includes(f.key);
                          return (
                            <button key={f.key} type="button" disabled={savingBuilder} onClick={() => setSelectedFields((prev) => sel ? prev.filter((k) => k !== f.key) : [...prev, f.key])} style={{
                              padding: `${spacing.xs} ${spacing.sm}`, borderRadius: borderRadius.md, border: 'none',
                              backgroundColor: sel ? colors.primary.yellow : colors.neutral.gray800,
                              color: sel ? colors.neutral.black : colors.neutral.gray400,
                              fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
                              cursor: savingBuilder ? 'not-allowed' : 'pointer', fontFamily: typography.fontFamily.body,
                            }}>
                              {f.label_ms} <span style={{ opacity: 0.6 }}>({f.type})</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {!entityLoading && builderForm.entityType && entityFields.length === 0 && (
                    <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                      Tiada medan tersedia untuk entiti ini. / No fields available for this entity.
                    </p>
                  )}
                </div>

                {builderError && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{builderError}</p>}
                <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
                  <Button variant="primary" size="sm" onClick={() => void handleSaveBuilder()} disabled={savingBuilder} loading={savingBuilder}>
                    {savingBuilder ? 'Menyimpan…' : 'Simpan Laporan / Save Report'}
                  </Button>
                  <Button variant="ghost" size="sm" disabled={savingBuilder} onClick={() => { setBuilderForm({ nameEn: '', nameMs: '', entityType: '', scope: 'SCHOOL', isShared: false }); setSelectedFields([]); setEntityFields([]); setBuilderError(null); }}>
                    Set Semula
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

function LF({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={style}><label style={labelSt}>{label}</label>{children}</div>;
}
function Pill({ label, color }: { label: string; color: string }) {
  return <span style={{ display: 'inline-block', padding: `1px ${spacing.xs}`, backgroundColor: `${color}22`, color, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>{label}</span>;
}
function ErrorBanner({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
      <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{msg}</p>
      <Button variant="ghost" size="sm" onClick={onRetry} style={{ marginTop: spacing.xs }}>Cuba Lagi</Button>
    </div>
  );
}
function LoadingBox() { return <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>; }
function EmptyBox({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: spacing.sm }}>{icon}</div>
      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{title}</p>
      <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>{body}</p>
    </div>
  );
}
const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return { width: '100%', backgroundColor: colors.neutral.black, border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md, color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`, fontSize: typography.fontSize.sm, outline: 'none', boxSizing: 'border-box', fontFamily: typography.fontFamily.body };
}
